import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import CarteModule from "../components/CarteModule";
import { accueilVisible, modulesVisibles } from "../modulesV2";
import { getApplications } from "../services/api";

// ===================================================================
// The Crowd v2 — Accueil
//
// Vue unique : une carte par module ou par regroupement, filtree selon
// Collaborateurs.role (cf. modulesV2.js). Un regroupement ouvre sa page
// intermediaire (/v2/<id>) ; un module est ouvert par App.js, qui gere
// la bascule de vue tant que les modules ne sont pas branches sur la v2.
//
// Sous les modules, la section Applications : raccourcis vers les outils
// de la Direction Technique, en mini-cartes par categorie, ouverts dans un
// nouvel onglet. Lus en base (GET /api/applications) ; les directeurs les
// gerent depuis Pilotage › Liens.
// ===================================================================

function piedGroupe(modules) {
  const total = modules.length;
  const aCadrer = modules.filter((m) => m.aCadrer).length;
  const base = `${total} module${total > 1 ? "s" : ""}`;
  return aCadrer > 0 ? `${base} · ${aCadrer} à cadrer` : base;
}

function CarteApplication({ lien }) {
  return (
    <a className="v2-appli" href={lien.url} target="_blank" rel="noopener noreferrer" title={lien.url}>
      <span className="v2-appli-initiale" aria-hidden="true">{lien.nom.charAt(0).toUpperCase()}</span>
      <span className="v2-appli-texte">
        <span className="v2-appli-nom">
          {lien.nom}
          {lien.reseau_local && <span className="v2-badge">Réseau local</span>}
        </span>
        <span className="v2-appli-desc">{lien.description}</span>
      </span>
    </a>
  );
}

function AccueilV2({ collaborateur, nomUtilisateur, onOuvrirModule }) {
  const navigate = useNavigate();
  const entrees = accueilVisible(collaborateur);
  const [applications, setApplications] = useState(null);
  const [erreurApplications, setErreurApplications] = useState(false);

  useEffect(() => {
    let annule = false;
    getApplications()
      .then((c) => { if (!annule) setApplications(c.filter((cat) => cat.liens.length > 0)); })
      .catch(() => { if (!annule) setErreurApplications(true); });
    return () => { annule = true; };
  }, []);

  return (
    <div className="v2-page">
      <section className="v2-entete">
        <h1 className="v2-titre">Bonjour{nomUtilisateur ? ` ${nomUtilisateur}` : ""}</h1>
        <p className="v2-sous-titre">Vos modules, selon votre profil.</p>
      </section>

      <section>
        <div className="v2-section-titre">
          <h2>Modules</h2>
          <span className="v2-section-filet" />
        </div>

        <div className="v2-grille v2-grille-accueil">
          {entrees.map((e) =>
            e.type === "groupe" ? (
              <CarteModule
                key={e.id}
                taille="grande"
                titre={e.titre}
                description={e.description}
                icone={e.icone}
                pied={piedGroupe(modulesVisibles(e, collaborateur))}
                onClick={() => navigate(`/v2/${e.id}`)}
              />
            ) : (
              <CarteModule
                key={e.id}
                taille="grande"
                titre={e.titre}
                description={e.description}
                icone={e.icone}
                pied="Ouvrir"
                onClick={() => onOuvrirModule(e)}
              />
            )
          )}
        </div>
      </section>

      <hr className="v2-accueil-separateur" />

      <section aria-labelledby="v2-accueil-applications">
        <div className="v2-section-titre">
          <h2 id="v2-accueil-applications">Applications</h2>
          <span className="v2-section-filet" />
        </div>

        {erreurApplications && <p className="v2-cellule-discrete">Applications indisponibles pour le moment.</p>}
        {!applications && !erreurApplications && <div className="v2-chargement">Chargement…</div>}
        {applications && applications.length === 0 && <p className="v2-cellule-discrete">Aucune application.</p>}
        {applications && applications.length > 0 && (
          <div className="v2-applis">
            {applications.map((c) => (
              <div key={c.id} className="v2-applis-categorie">
                <h3 className="v2-applis-titre">{c.titre}</h3>
                <div className="v2-applis-grille">
                  {c.liens.map((l) => <CarteApplication key={l.id} lien={l} />)}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export default AccueilV2;
